import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, Button, Typography, Alert, Grid, List, ListItem, ListItemIcon, ListItemText, IconButton, InputAdornment } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../hooks/useAuth';
import { useUser } from '../../context/UserContext';
import { changePassword } from '../../api/authService';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ChangePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const {user, updateUser} = useUser();
  const [email, setEmail] = useState(user.email); // Stores the user's email  
  const { accessToken, handleLogin } = useAuth();

  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noSpaces: true, // No spaces allowed
  });

  const [showPassword, setShowPassword] = useState(false); // State to manage password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State to manage confirm password visibility

 // const { accessToken, signOut } = useAuth();
  
const validatePassword = (password) => {
  const rules = {
    length: password.length >= 8,
    maxLength: password.length <= 20, // Check for maximum length
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    noSpaces: !/\s/.test(password), // Check for spaces
  };
  setPasswordRules(rules);
  return Object.values(rules).every(Boolean);
};

const handlePasswordChange = (e) => {
  const newPassword = e.target.value;
  setPassword(newPassword);
  validatePassword(newPassword);
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    

    if (validatePassword(password)) {
      // Submit the password change request
      //setSuccess("Password changed successfully!");
      setError("");
    } else {
      setError("Please satisfy all password rules.");
      return;
    }
    // Basic validation
    if (!password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    try {
      // Change password and call event registration system      
      const { response, data } = await changePassword(password, email, accessToken);

      console.log("validate change password :", response);
    
      if (response.ok) {
        setSuccess("Password changed successfully!");
        handleLogin(); // Set the IsLoggedIn state to true
        navigate('/EventTypesForm');
        setError("");
        setPassword("");
        setConfirmPassword("");
      } else {
        if (response.status === 400) {
          setError("Bad request. Please check your input.");
        } else if (response.status === 401) {
          setError("Unauthorized. Please log in again.");
        } else if (response.status === 403) {
          setError("Access token expired. Please log in again.");
        } 
        else if (response.status === 500) {
          setError("Server error. Please try again later.");
        } else {
          setError( "Failed to change password.");
        }
      }
    } catch (err) {
      setError("An error occurred. Please try again." + err);
    }
  };

  return (
    <>
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="#f9f9f9"
    >
    <Box
      sx={{
        maxWidth: 400,
        margin: "auto",
        padding: 3,
        border: "1px solid #ddd",
        borderRadius: 2,
        boxShadow: 2,
        mt: 5,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
        Change/Set Password
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>        

          <Grid item xs={12}>
            <TextField
              label="New Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={password}
              onChange={handlePasswordChange}
              required
              inputProps={{
                maxLength: 20, // Limit the password length to 20 characters
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit" fullWidth>
              Change/Set Password
            </Button>
          </Grid>
          <List>
          <ListItem>
            <ListItemIcon>
              {passwordRules.length ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="At least 8 characters" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {passwordRules.uppercase ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="At least one uppercase letter" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {passwordRules.lowercase ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="At least one lowercase letter" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {passwordRules.number ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="At least one number" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {passwordRules.specialChar ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="At least one special character" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {passwordRules.noSpaces ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary="No spaces" />
          </ListItem>
        </List>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}
        </Grid>
      </form>
    </Box>
    </Box>

    </>
  );
};

export default ChangePassword;
